import { Box, Stack, Text, UnstyledButton } from '@mantine/core';
import type { ReactNode } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

interface TabDefinition {
  to: string;
  label: string;
  icon: ReactNode;
}

interface BottomTabBarProps {
  tabs: TabDefinition[];
}

/**
 * Thumb-reachable bottom navigation — the primary way drivers move around
 * the app on a phone. Deliberately not a Mantine AppShell navbar (that's a
 * desktop side-rail pattern); this is hand-rolled for the mobile-first,
 * fixed-bottom tab-bar pattern native apps use.
 */
export const BottomTabBar = ({ tabs }: BottomTabBarProps) => {
  const location = useLocation();
  const navigate = useNavigate();

  return (
    <Box
      component="nav"
      style={{
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        zIndex: 100,
        display: 'flex',
        backgroundColor: 'var(--app-bg)',
        borderTop: '1px solid var(--mantine-color-ink-3)',
        paddingBottom: 'var(--safe-bottom)',
      }}
    >
      {tabs.map((tab) => {
        // Exact match for top-level tabs like "/" and "/profile", but prefix
        // match for section roots like "/admin" so nested admin routes
        // (e.g. /admin/daily, /admin/monthly) still highlight the tab.
        const active =
          tab.to === '/'
            ? location.pathname === '/'
            : location.pathname === tab.to || location.pathname.startsWith(`${tab.to}/`);
        return (
          <UnstyledButton
            key={tab.to}
            onClick={() => navigate(tab.to)}
            style={{
              flex: 1,
              position: 'relative',
              padding: '10px 0 8px',
              borderTop: `2px solid ${active ? 'var(--mantine-color-signal-6)' : 'transparent'}`,
              transition: 'border-color 120ms ease',
            }}
          >
            <Stack align="center" gap={2}>
              <Box
                style={{
                  color: active ? 'var(--mantine-color-signal-6)' : 'var(--mantine-color-ink-7)',
                  transition: 'color 120ms ease',
                }}
              >
                {tab.icon}
              </Box>
              <Text
                c={active ? 'signal.7' : 'ink.7'}
                fw={600}
                size="10px"
                style={{ letterSpacing: '0.06em', textTransform: 'uppercase' }}
              >
                {tab.label}
              </Text>
            </Stack>
          </UnstyledButton>
        );
      })}
    </Box>
  );
};
